import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EmptyState,
  SectionLabel,
  Spinner,
} from '@wundo/ui';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import { getDocument } from '@/lib/documents.ts';
import type { Document } from '@/types/documents.ts';

export interface DocumentChunksDialogProps {
  /** The row whose chunks to show; `null` closes the dialog. */
  document: Document | null;
  onClose: () => void;
}

/**
 * The pieces a document was cut into on its way to being searchable.
 *
 * Read-only and unpaged: a document has tens of chunks, not thousands, and what
 * this answers — did the text come out of the file intact — is a question you
 * scroll rather than query.
 */
export function DocumentChunksDialog({ document, onClose }: DocumentChunksDialogProps) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['documents', document?.id, 'chunks'],
    queryFn: () => getDocument(document!.id),
    enabled: document !== null,
  });

  return (
    <Dialog open={document !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size='lg' layout='scroll'>
        <DialogHeader>
          <DialogTitle>{document?.file_name ?? 'Document chunks'}</DialogTitle>
          <DialogClose aria-label='Close' />
        </DialogHeader>

        <div className='px-5 py-4.5'>
          {isPending && (
            <p role='status' className='flex justify-center py-7 text-ink-muted'>
              <Spinner className='size-6' />
              <span className='sr-only'>Loading</span>
            </p>
          )}

          {isError && (
            <p
              role='alert'
              className='py-4 text-center text-sm font-semibold text-danger'
            >
              Failed to load document chunks
            </p>
          )}

          {data && (
            <>
              {data.summary && (
                <section className='mb-4.5 rounded-md bg-fill-faint p-3.5'>
                  <SectionLabel>Summary</SectionLabel>
                  <p className='mt-2 text-sm whitespace-pre-wrap text-ink-body'>
                    {data.summary}
                  </p>
                </section>
              )}

              <dl className='mb-4.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm'>
                <dt className='font-semibold text-ink-muted'>File</dt>
                <dd className='text-ink-body'>{data.file_name}</dd>
                <dt className='font-semibold text-ink-muted'>Type</dt>
                <dd className='text-ink-body'>{data.file_mime_type}</dd>
                <dt className='font-semibold text-ink-muted'>Size</dt>
                <dd className='text-ink-body'>{formatFileSize(data.file_size)}</dd>
                <dt className='font-semibold text-ink-muted'>Chunks</dt>
                <dd className='text-ink-body'>{data.chunks.length}</dd>
              </dl>

              {data.chunks.length === 0 ? (
                <EmptyState
                  headingLevel='h3'
                  title='No chunks'
                  description='This document has not produced any chunks.'
                />
              ) : (
                <ol className='flex flex-col gap-3'>
                  {data.chunks.map((chunk, index) => (
                    <li key={chunk.id} className='rounded-md border border-line p-3.5'>
                      <div className='mb-2 flex items-center justify-between gap-3'>
                        <SectionLabel>Chunk {index + 1}</SectionLabel>
                        <span className='text-xs font-semibold text-ink-muted'>
                          {chunk.page_number ? `Page ${chunk.page_number}` : ''}
                          {chunk.page_number && chunk.chunk_index ? ' · ' : ''}
                          {chunk.chunk_index ? `Index ${chunk.chunk_index}` : ''}
                        </span>
                      </div>
                      <p className='text-sm whitespace-pre-wrap text-ink-body'>
                        {chunk.content}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
